import { Button, ComboboxData, Group, Select, Stack, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SchoolJobFamilyForm, UpdateSchoolJobFamilyForm } from 'types/dacum/schoolManagement';
import { UseMutationResult, useQuery } from '@tanstack/react-query';
import { useSchoolJobFamilyStore } from 'store/schoolJobFamily';
import { schoolService } from 'services/dacum/school.service';

interface CreateJobFamilyFormProps {
  createJobFamily: UseMutationResult<any, Error, SchoolJobFamilyForm>;
  updateJobFamily: UseMutationResult<any, Error, UpdateSchoolJobFamilyForm>;
}

const CreateJobFamilyForm = ({ createJobFamily, updateJobFamily }: CreateJobFamilyFormProps) => {
  const navigate = useNavigate();
  const { formData, mode, setFormData } = useSchoolJobFamilyStore();
  const [schoolCompetencies, setschoolCompetencies] = useState<ComboboxData>([]);

  const { data: dataSchoolCompetency, isPending: loadingSchoolCompetency } = useQuery({
    queryKey: ['getBands'],
    queryFn: () => schoolService.getSchoolCompetency({ pageSize: 10000, pageNumber: 1 })
  });

  useEffect(() => {
    if (dataSchoolCompetency?.data) {
      const formattedData: ComboboxData = dataSchoolCompetency?.data.map((item) => {
        return { value: item.msSchoolCompetencyManagementID, label: item.schoolName };
      });

      setschoolCompetencies(formattedData);
    }
  }, [dataSchoolCompetency?.data]);

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<SchoolJobFamilyForm>({
    defaultValues: {
      msSchoolCompetencyManagementID: formData.msSchoolCompetencyManagementID || '',
      jobFamilyID: formData.jobFamilyID || ''
    },
    mode: 'all',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    reset({
      msSchoolCompetencyManagementID: formData.msSchoolCompetencyManagementID || '',
      jobFamilyID: formData.jobFamilyID || ''
    });
    setValue('msSchoolCompetencyManagementID', formData.msSchoolCompetencyManagementID || '');
    setValue('jobFamilyID', formData.jobFamilyID || '');
  }, [formData, reset, setValue]);

  const onSubmit = (data: SchoolJobFamilyForm) => {
    setFormData({
      ...data,
      msSchoolCompetencyManagementID: data.msSchoolCompetencyManagementID,
      jobFamilyID: formData.jobFamilyID
    });

    if (mode === 'add') {
      createJobFamily.mutate(data);
    } else if (mode === 'edit') {
      updateJobFamily.mutate({
        ...data,
        trSchoolJobFamilyID: formData.trSchoolJobFamilyID as string
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={32}>
          <Stack gap={16}>
            <Controller
              name='msSchoolCompetencyManagementID'
              control={control}
              render={({ field }) => (
                <Select
                  label='School'
                  placeholder='Select school'
                  searchable
                  data={schoolCompetencies}
                  disabled={loadingSchoolCompetency}
                  error={errors.msSchoolCompetencyManagementID?.message}
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                />
              )}
            />

            <Controller
              name='jobFamilyID'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Job Family'
                  placeholder='Enter job family'
                  error={errors.jobFamilyID?.message}
                  disabled={mode === 'view'}
                  {...field}
                />
              )}
            />
          </Stack>

          <Group justify='flex-end'>
            <Button variant='outline' onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              type='submit'
              loading={isSubmitting || createJobFamily.isPending || updateJobFamily.isPending}
              size='md'
              leftSection={<IconDeviceFloppy />}
            >
              {mode === 'add' ? 'Create' : 'Update'} Job Family
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
};

export default CreateJobFamilyForm;
